import { useMutation } from '@tanstack/react-query';
import {
  AnalyticsAuthEventNames,
  AnalyticsAuthMode,
  maskEmail,
  trackAuthEvent,
} from '@tectonic/analytics';
import { getErrorMessage } from '@tectonic/errors';
import { checkUserExists, requestOtp } from '@tectonic/remix-client-network';
import { ElemasonAuthMode, ElemasonAuthStep } from '@tectonic/types';
import { isEmpty } from 'lodash-es';
import { useToast } from '../../../core/ElemasonEntry/Toast';
import { useAuthStore } from '../../../store';

import type { ElemasonAuthEmailOtpSignInFormWidget } from '@tectonic/types';
import type { EmailInputFormValue } from '../../../components';

const requestEmailOtp = async (email: string) => {
  const otpResponse = await requestOtp({ email });
  if (otpResponse.error) {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw otpResponse.error;
  }
  return otpResponse;
};

const useEmailOtpSignInForm = (
  widget: ElemasonAuthEmailOtpSignInFormWidget
) => {
  const wData = widget.data!;
  const setStep = useAuthStore((state) => state.setStep);
  const { showToast } = useToast();
  const isSignUpRequired = wData.isSignUpRequired ?? true;

  const submitWithSignUpRequired = async (value: EmailInputFormValue) => {
    const { email } = value;
    try {
      const response = await checkUserExists({ email });
      if (response.error || isEmpty(response.data)) {
        throw response.error;
      }

      await requestEmailOtp(email);
      const isNewUser = !response.data.exists;

      const title = isNewUser
        ? wData.messages.signUpOtpSendSuccess
        : wData.messages.signInOtpSendSuccess;
      showToast({ title });

      trackAuthEvent(AnalyticsAuthEventNames.SIGN_IN_SEND_OTP_SUCCESS, {
        method: AnalyticsAuthMode.EMAIL_OTP,
        email: maskEmail(email),
        isNewUser,
      });

      if (isNewUser) {
        // User doesn't exist in our system. Let's redirect them to auth flow.
        setStep(ElemasonAuthStep.EMAIL_OTP_CONFIRM, {
          email,
          mode: ElemasonAuthMode.SIGN_UP,
        });
      } else {
        setStep(ElemasonAuthStep.EMAIL_OTP_CONFIRM, { email });
      }
    } catch (error) {
      trackAuthEvent(AnalyticsAuthEventNames.SIGN_IN_SEND_OTP_ERROR, {
        method: AnalyticsAuthMode.EMAIL_OTP,
        email: maskEmail(email),
        error,
      });
      showToast({ title: getErrorMessage(error, wData.errorMessages) });
    }
  };

  const submitWithoutSignUpRequired = async (value: EmailInputFormValue) => {
    const { email } = value;
    try {
      await requestEmailOtp(email);
      setStep(ElemasonAuthStep.EMAIL_OTP_CONFIRM, { email });
      trackAuthEvent(AnalyticsAuthEventNames.SIGN_IN_SEND_OTP_SUCCESS, {
        method: AnalyticsAuthMode.EMAIL_OTP,
        email: maskEmail(email),
      });
    } catch (error) {
      trackAuthEvent(AnalyticsAuthEventNames.SIGN_IN_SEND_OTP_ERROR, {
        method: AnalyticsAuthMode.EMAIL_OTP,
        email: maskEmail(email),
        error,
      });
      showToast({ title: getErrorMessage(error, wData.errorMessages) });
    }
  };

  const { isPending: isSubmitting, mutate: onSubmit } = useMutation({
    mutationFn: (value: EmailInputFormValue) => {
      const { email } = value;
      trackAuthEvent(AnalyticsAuthEventNames.SIGN_IN_SEND_OTP_REQUEST, {
        method: AnalyticsAuthMode.EMAIL_OTP,
        email: maskEmail(email),
      });
      if (isSignUpRequired) {
        return submitWithSignUpRequired(value);
      }
      return submitWithoutSignUpRequired(value);
    },
    mutationKey: [],
  });

  return { isSubmitting, onSubmit };
};

export { useEmailOtpSignInForm };
